import { firebase } from './firebase.js';
import { readEnv } from './env.js';
import { platform } from './device.js';

/**
 * Apple requires an app that offers Sign in with Apple and account deletion to
 * revoke the user's Apple token when the account is deleted (App Store
 * Review 5.1.1(v)).
 *
 * Revocation needs a fresh authorization code — they live five minutes — so
 * the user confirms with Apple once more (Face ID / passcode). The code goes
 * to Firebase Auth's `accounts:revokeToken`, which exchanges and revokes it
 * with Apple using the posture Sign in with Apple key configured on the Apple
 * provider (NATIVE.md). The JS SDK's `revokeAccessToken` only takes access
 * tokens, and native Apple sign-in yields a code, hence the direct call — the
 * same request the iOS Firebase SDK makes.
 */
export class AppleRevokeError extends Error {
  constructor(
    message: string,
    readonly cancelled: boolean,
  ) {
    super(message);
    this.name = 'AppleRevokeError';
  }
}

export function needsAppleRevoke(): boolean {
  const user = firebase().auth.currentUser;
  return platform() === 'ios' && Boolean(user?.providerData.some((p) => p.providerId === 'apple.com'));
}

export async function revokeAppleToken(): Promise<void> {
  const user = firebase().auth.currentUser;
  if (!user) throw new AppleRevokeError('Not signed in.', false);

  const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
  let code: string | undefined;
  try {
    const { credential } = await FirebaseAuthentication.signInWithApple();
    code = credential?.authorizationCode;
  } catch (error) {
    const cancelled = /cancel/i.test(String((error as { message?: string }).message ?? ''));
    throw new AppleRevokeError(
      cancelled
        ? 'Deleting an Apple-linked account needs one more confirmation with Apple.'
        : 'Apple could not confirm your account. Try again.',
      cancelled,
    );
  }
  if (!code) throw new AppleRevokeError('Apple did not return a confirmation code. Try again.', false);

  const response = await fetch(
    `https://identitytoolkit.googleapis.com/v2/accounts:revokeToken?key=${encodeURIComponent(readEnv().apiKey)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        providerId: 'apple.com',
        tokenType: 'CODE',
        token: code,
        idToken: await user.getIdToken(),
      }),
    },
  );
  if (!response.ok) throw new AppleRevokeError('Could not disconnect your Apple account. Try again.', false);
}
